import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from './fs';

/**
 * Warn before typed-but-unsaved work is thrown away.
 *
 * Every screen with a Save button can be left with edits still on it — a tab
 * in the row above, a link in the top bar, a browser reload — and until now
 * leaving simply lost them silently. The guard is deliberately ONE shape used
 * everywhere: a screen either has a Save button, in which case it warns, or it
 * saves as it goes, in which case there is nothing to warn about.
 *
 * Two halves:
 *
 *   useDirty         what "changed" means — a snapshot of the editable state
 *                    compared against the one that came back from the server.
 *   useUnsavedGuard  what happens when somebody tries to leave while dirty.
 *
 * There is no data router here, so react-router's useBlocker is unavailable:
 * outbound navigation is caught by listening for anchor clicks in the CAPTURE
 * phase, before the Link's own handler runs. Anything that navigates by
 * calling navigate() directly asks the guard itself, through `confirm`.
 */

/** A snapshot of the editable state, in whatever shape the screen finds natural. */
type Snapshot = unknown;

const same = (a: Snapshot, b: Snapshot) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Clicks already answered by a guard.
 *
 * Two guards can be live at once — a dirty form draft in the workspace shell
 * and a dirty Settings tab inside it — and both listen on `document`, where
 * stopPropagation does not stop a sibling listener. Without this, one click on
 * a link out would raise two identical dialogs on top of each other. Whichever
 * guard sees the click first answers for it; the question is the same either
 * way, and leaving loses everything unsaved regardless of which screen asked.
 */
const claimed = new WeakSet<Event>();

export interface Dirty {
  /** True once the snapshot differs from the last saved one. */
  dirty: boolean;
  /** Call after a successful save — the current snapshot becomes the baseline. */
  markSaved: () => void;
}

/**
 * Dirty-tracking by comparison rather than by flag.
 *
 * A `setDirty(true)` on every onChange is the other way to do this, and it is
 * wrong twice over: it has to be remembered on every one of a settings page's
 * forty controls, and it calls a field typed into and back out again "changed".
 * Comparing a snapshot needs one call site and answers honestly.
 *
 * `ready` guards the load: a page whose state starts empty and is filled in by
 * a fetch would otherwise baseline the empty version and call the arrival of
 * real data an edit.
 */
export function useDirty(snapshot: Snapshot, ready = true): Dirty {
  const [baseline, setBaseline] = useState<{ v: Snapshot } | null>(null);
  /**
   * markSaved cannot read the new snapshot itself: it is called from the save
   * handler, alongside the setState calls that put the server's answer on
   * screen, and those have not rendered yet. So it asks for a re-baseline and
   * an effect takes it once they have — which also means a server that
   * normalized what it was sent (a trimmed name, a canonical slug) baselines
   * what it actually stored rather than what was typed.
   */
  const [rebaseline, setRebaseline] = useState(0);
  const latest = useRef(snapshot);
  latest.current = snapshot;

  useEffect(() => {
    if (!ready) { setBaseline(null); return; }
    // Read the snapshot NOW, as the render that made the page ready left it —
    // not inside the updater. React runs an updater when it renders it, and
    // this effect's update is ordinary priority: a keystroke that lands first
    // is rendered first, so an updater reading `latest` would find the typed
    // value and make the edit the baseline (the page read clean, the guard
    // let them leave).
    const v = latest.current;
    setBaseline((b) => (b && !rebaseline ? b : { v }));
    // The snapshot changes on every keystroke; only readiness and an explicit
    // markSaved may move the baseline.
  }, [ready, rebaseline]);

  /**
   * Baselined twice on purpose (R8): once now, from the snapshot as it stands,
   * so the very next render already reads clean — a click that lands between
   * the save's render and the passive effect below must not be blocked — and
   * once more after that render, so a server that normalized what it was sent
   * still wins.
   */
  const markSaved = useCallback(() => {
    setBaseline({ v: latest.current });
    setRebaseline((n) => n + 1);
  }, []);

  return { dirty: !!baseline && !same(baseline.v, snapshot), markSaved };
}

export interface UnsavedGuardOptions {
  /** Whether there is anything to lose. */
  dirty: boolean;
  /**
   * Paths that count as staying put — the form workspace holds one working
   * copy across its own tabs, so moving between them loses nothing.
   */
  within?: string;
  /** What is unsaved, named in the dialog: "Your changes to Settings". */
  what?: string;
  /** Offered as "Save and leave" when the screen can save without more input. */
  onSave?: () => Promise<unknown> | unknown;
  saveLabel?: string;
  /**
   * The dialog's sentence and its leave button, for work that leaving does
   * NOT lose — Site content's draft is kept on the server, so what it has to
   * say is "not live yet", never "you will lose this". Default: the words
   * below, which are true of every other screen.
   */
  body?: ReactNode;
  leaveLabel?: string;
  /** Disables the dialog's buttons while a save is in flight. */
  busy?: boolean;
}

export interface UnsavedGuard {
  /** Render this — it is the dialog, and nothing while there is nothing pending. */
  node: ReactNode;
  /**
   * Run `go` now if the screen is clean, or ask first if it is not. For
   * navigation the click listener cannot see: an in-page tab, a drawer's own
   * close button, a navigate() from a handler.
   */
  confirm: (go: () => void) => void;
}

export function useUnsavedGuard({
  dirty, within, what, onSave, saveLabel, body, leaveLabel, busy,
}: UnsavedGuardOptions): UnsavedGuard {
  const navigate = useNavigate();
  /**
   * Held through an updater, because React CALLS a function handed to a
   * setter — which would run the very navigation the guard exists to hold
   * back, and store its undefined result as the pending one.
   */
  const [pending, setPending] = useState<(() => void) | null>(null);

  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const navRef = useRef(navigate);
  navRef.current = navigate;

  const confirm = useCallback((go: () => void) => {
    if (!dirtyRef.current) { go(); return; }
    setPending(() => go);
  }, []);

  useEffect(() => {
    if (!dirty) return;

    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };

    const onClick = (e: MouseEvent) => {
      // Anything the browser would not treat as an in-page navigation is not
      // ours to intercept: a middle click, a modified click, a new tab, a
      // download, a cross-origin link, or a click something else has handled.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      // Staying on this very path is not leaving: an href of "#…" is a control
      // wearing a link's clothes (the platform settings tabs, "Change
      // password"), and a re-click of the tab you are already on goes nowhere.
      if (url.pathname === window.location.pathname
        && (url.search === window.location.search || (!!url.hash && !url.search))) return;
      if (within && url.pathname.startsWith(within)) return;
      if (claimed.has(e)) return;
      claimed.add(e);

      e.preventDefault();
      e.stopPropagation();
      const to = url.pathname + url.search + url.hash;
      setPending(() => () => navRef.current(to));
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty, within]);

  // Saving elsewhere while the dialog is open (the draft bar can do it) leaves
  // a question about nothing — close it rather than strand it.
  useEffect(() => { if (!dirty && pending) setPending(null); }, [dirty, pending]);

  const go = () => { const run = pending; setPending(null); run?.(); };

  const node = pending ? (
    <Modal
      title="You have unsaved changes"
      narrow
      onClose={() => setPending(null)}
      footer={(
        <>
          {onSave && (
            <button
              className="fs-btn primary" disabled={busy}
              onClick={async () => {
                // A save that fails leaves the dialog up and the screen shows
                // why — leaving anyway would lose the work the save just
                // failed to store.
                try { await onSave(); } catch { return; }
                go();
              }}
            >
              {saveLabel || 'Save and leave'}
            </button>
          )}
          <button className={`fs-btn ${leaveLabel ? 'neutral' : onSave ? 'danger' : 'primary'}`} disabled={busy} onClick={go}>
            {leaveLabel || (onSave ? 'Leave without saving' : 'Leave and lose them')}
          </button>
          <span style={{ flex: 1 }} />
          <button className="fs-btn neutral" onClick={() => setPending(null)}>Stay here</button>
        </>
      )}
    >
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
        {body || <>{what || 'Your changes on this screen'} have not been saved yet. Leaving now loses them.</>}
      </p>
    </Modal>
  ) : null;

  return { node, confirm };
}

/**
 * What the section currently on screen has unsaved, reported upward.
 *
 * A page whose tab row swaps one section for another WITHOUT a route change
 * (platform settings) gets no help from the click listener — a half-typed
 * mail server would simply vanish on the way to Storage. Each section hands
 * the page its dirty state and a way to save, and the tabs ask before they
 * switch, through the guard's `confirm`.
 */
export interface SectionEdits { dirty: boolean; save: () => Promise<unknown> }

/** The two effects every section runs to report itself; `report` must be stable. */
export function useReportEdits(
  report: ((e: SectionEdits | null) => void) | undefined,
  dirty: boolean,
  save: () => Promise<unknown>,
) {
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => () => report?.(null), [report]);
  useEffect(() => { report?.({ dirty, save: () => saveRef.current() }); }, [report, dirty]);
}
